/**
 * Storezone — Login Page
 *
 * Professional light-mode login using the design system tokens.
 * Applies AuthLayout (centered card on light gray background).
 */

import { type FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { AuthLayout } from '@/layouts/AuthLayout'
import { Button, Input } from '@/components/ui'
import { Icons } from '@/lib/icons'
import './LoginPage.css'

export function LoginPage() {
  const { signIn } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const me = await signIn(email, password)
      if (me?.is_superadmin) navigate('/platform', { replace: true })
      else if (me?.memberships?.[0]?.role_key === 'seller') navigate('/pos', { replace: true })
      else navigate('/admin', { replace: true })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign-in failed. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className="sz-login">

        {/* Header */}
        <div className="sz-login__header">
          <div className="sz-login__logo">
            <span className="sz-login__logo-mark">SZ</span>
          </div>
          <h1 className="sz-login__title">Welcome to Storezone</h1>
          <p className="sz-login__subtitle">Sign in to your account to continue</p>
        </div>

        {/* Form */}
        <form className="sz-login__form" onSubmit={handleSubmit} noValidate>
          <Input
            id="login-email"
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            prefixEl={<Icons.Mail size={16} />}
            required
            fullWidth
          />

          <Input
            id="login-password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            prefixEl={<Icons.Lock size={16} />}
            suffixEl={
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--sz-text-3)', display: 'flex', alignItems: 'center' }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
              </button>
            }
            required
            fullWidth
          />

          {error && (
            <div className="sz-login__error" role="alert">
              {error}
            </div>
          )}

          <Button
            id="login-submit"
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
          >
            Sign in
          </Button>
        </form>

        {/* Footer */}
        <p className="sz-login__footer">
          Storezone &copy; {new Date().getFullYear()} SmartZone Leaders
        </p>
      </div>
    </AuthLayout>
  )
}
